import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useLeague } from '@/contexts/LeagueContext';
import { format, formatDistanceToNow } from 'date-fns';
import { Search, Filter, Clock, ChevronDown, ArrowDownUp, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Activity {
  id: string;
  team_id: string;
  activity_type: string;
  player_name: string | null;
  details: {
    faab_cost?: number;
    dropped_player?: string;
    added_player?: string;
    trade_partner_team?: string;
    receiver_team?: string;
    proposer_team?: string;
    trade_sent_players?: string[];
    trade_received_players?: string[];
    offered?: string[];
    requested?: string[];
    proposer_sent?: string[];
    receiver_sent?: string[];
    sabotage_name?: string;
    sabotage_icon?: string;
    target_team?: string;
    player_name?: string;
    previous_slot?: string;
    proposer_team_id?: string;
    receiver_team_id?: string;
    [key: string]: any;
  };
  created_at: string;
}

const FILTER_TYPES = [
  { id: 'all', label: 'All', icon: '📋' },
  { id: 'add_drop', label: 'Add/Drop', icon: '🔄' },
  { id: 'trade', label: 'Trades', icon: '🤝' },
  { id: 'sabotage', label: 'Sabotage', icon: '💣' },
] as const;

type FilterType = typeof FILTER_TYPES[number]['id'];

const ACTIVITY_META: Record<string, { icon: string; label: string; accent: string }> = {
  add: { icon: '➕', label: 'Add', accent: 'text-green-400' },
  drop: { icon: '➖', label: 'Drop', accent: 'text-destructive' },
  add_drop: { icon: '🔄', label: 'Add/Drop', accent: 'text-primary' },
  trade: { icon: '🤝', label: 'Trade', accent: 'text-blue-400' },
  trade_proposed: { icon: '📨', label: 'Trade Proposed', accent: 'text-blue-400' },
  trade_completed: { icon: '✅', label: 'Trade Done', accent: 'text-green-400' },
  sabotage: { icon: '💣', label: 'Sabotage', accent: 'text-amber-400' },
};

const PAGE_SIZE = 5;

const MLBBetaTransactionHistory = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [teamNames, setTeamNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [sortNewest, setSortNewest] = useState(true);
  const { activeLeague } = useLeague();

  const getFilterTypes = (f: FilterType): string[] => {
    switch (f) {
      case 'add_drop': return ['add', 'drop', 'add_drop'];
      case 'trade': return ['trade', 'trade_proposed', 'trade_completed'];
      case 'sabotage': return ['sabotage'];
      default: return [];
    }
  };

  const fetchActivities = useCallback(async (append = false) => {
    if (!activeLeague?.id) return;
    if (append) setLoadingMore(true); else setLoading(true);

    let query = supabase
      .from('league_activity')
      .select('*')
      .eq('league_id', activeLeague.id)
      .not('activity_type', 'in', '(move_to_il,activate_from_il,add_to_il,trade_proposed,trade_countered)')
      .order('created_at', { ascending: !sortNewest })
      .limit(PAGE_SIZE + 1);

    if (filter !== 'all') {
      query = query.in('activity_type', getFilterTypes(filter));
    }

    if (search.trim()) {
      query = query.or(`player_name.ilike.%${search.trim()}%`);
    }

    if (append && activities.length > 0) {
      const lastItem = activities[activities.length - 1];
      if (sortNewest) {
        query = query.lt('created_at', lastItem.created_at);
      } else {
        query = query.gt('created_at', lastItem.created_at);
      }
    }

    const { data, error } = await query;

    if (error) {
      console.error('Transaction history error:', error);
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    const items = (data || []) as unknown as Activity[];
    const hasNextPage = items.length > PAGE_SIZE;
    const pageItems = hasNextPage ? items.slice(0, PAGE_SIZE) : items;

    setHasMore(hasNextPage);

    if (append) {
      setActivities(prev => [...prev, ...pageItems]);
    } else {
      setActivities(pageItems);
    }

    // Resolve team names
    const existingIds = new Set(Object.keys(teamNames));
    const newIds = new Set(pageItems.map(a => a.team_id).filter(id => !existingIds.has(id)));
    if (newIds.size > 0) {
      const { data: teams } = await supabase
        .from('teams')
        .select('id, name')
        .in('id', Array.from(newIds));
      if (teams) {
        const map: Record<string, string> = {};
        teams.forEach(t => { map[t.id] = t.name; });
        setTeamNames(prev => ({ ...prev, ...map }));
      }
    }

    setLoading(false);
    setLoadingMore(false);
  }, [activeLeague?.id, filter, search, sortNewest, activities, teamNames]);

  // Reset and fetch on filter/search/sort change
  useEffect(() => {
    setActivities([]);
    setHasMore(true);
    fetchActivities(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLeague?.id, filter, search, sortNewest]);

  const renderTransaction = (activity: Activity) => {
    const d = activity.details;
    const teamName = teamNames[activity.team_id] || 'Unknown';
    const meta = ACTIVITY_META[activity.activity_type] || { icon: '📝', label: activity.activity_type, accent: 'text-muted-foreground' };
    const ts = new Date(activity.created_at);

    return (
      <div key={activity.id} className="flex items-start gap-3 p-4 border-b border-border/40 last:border-0 hover:bg-muted/20 transition-colors">
        {/* Icon */}
        <div className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center flex-shrink-0 mt-0.5">
          <span className="text-base">{meta.icon}</span>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <span className="text-sm font-semibold text-foreground">{teamName}</span>
            <Badge variant="outline" className={cn("text-[9px] px-1.5 h-4", meta.accent)}>
              {meta.label}
            </Badge>
            {(activity.details?.is_commissioner_action || activity.details?.commissioner_id) && (
              <Badge
                variant="outline"
                className="text-[10px] px-1.5 h-4 border-amber-500 text-amber-700 dark:text-amber-300 bg-amber-500/20 font-semibold gap-0.5"
                title="This action was taken by the league commissioner on behalf of the team owner"
              >
                <ShieldAlert className="h-2.5 w-2.5" />
                Commissioner
              </Badge>
            )}
          </div>
          {renderDetails(activity, d)}
        </div>

        {/* Timestamp */}
        <div className="flex-shrink-0 text-right">
          <p className="text-[10px] text-muted-foreground">
            {formatDistanceToNow(ts, { addSuffix: true })}
          </p>
          <p className="text-[9px] text-muted-foreground/60">
            {format(ts, 'MMM d, h:mm a')}
          </p>
        </div>
      </div>
    );
  };

  const renderDetails = (activity: Activity, d: Activity['details']) => {
    switch (activity.activity_type) {
      case 'add_drop':
        return (
          <p className="text-xs text-muted-foreground">
            <span className="text-green-400 font-medium">+{d.added_player}</span>
            {' / '}
            <span className="text-destructive font-medium">-{d.dropped_player}</span>
            {d.faab_cost != null && d.faab_cost > 0 && (
              <span className="text-primary ml-1.5 font-medium">${d.faab_cost} FAAB</span>
            )}
          </p>
        );

      case 'add':
        return (
          <p className="text-xs text-muted-foreground">
            Added <span className="text-green-400 font-medium">{activity.player_name}</span>
            {d.faab_cost != null && d.faab_cost > 0 && (
              <span className="text-primary ml-1.5">${d.faab_cost} FAAB</span>
            )}
          </p>
        );

      case 'drop':
        return (
          <p className="text-xs text-muted-foreground">
            Dropped <span className="text-destructive font-medium">{activity.player_name}</span>
          </p>
        );

      case 'trade_proposed':
      case 'trade':
        return (
          <div>
            <p className="text-xs text-muted-foreground">
              Proposed trade to <span className="font-medium text-foreground/80">{d.receiver_team || d.trade_partner_team}</span>
            </p>
            {d.offered && d.requested && (
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                Sends <span className="text-destructive">{(d.offered as string[]).join(', ')}</span>
                {' for '}
                <span className="text-green-400">{(d.requested as string[]).join(', ')}</span>
              </p>
            )}
          </div>
        );

      case 'trade_completed':
        return (
          <div>
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground/80">{d.proposer_team}</span>
              {' ↔ '}
              <span className="font-medium text-foreground/80">{d.receiver_team}</span>
            </p>
            {d.proposer_sent && d.receiver_sent && (
              <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                {(d.proposer_sent as string[]).join(', ')} ↔ {(d.receiver_sent as string[]).join(', ')}
              </p>
            )}
          </div>
        );

      case 'sabotage':
        return (
          <p className="text-xs text-muted-foreground">
            {d.sabotage_icon} <span className="font-medium">{d.sabotage_name}</span>
            {' → '}
            <span className="text-foreground/80">{d.target_team}</span>
          </p>
        );

      default:
        return (
          <p className="text-xs text-muted-foreground">{activity.player_name || 'Transaction'}</p>
        );
    }
  };

  return (
    <div className="space-y-4 pb-20 md:pb-0">
      {/* Header + Filters */}
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold font-heading text-foreground flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          Transactions
        </h2>
      </div>

      {/* Filters — horizontal scroll on narrow viewports w/ right-edge fade mask
          (MLB-021). At <420px the label hides and chips render icon-only with
          aria-label fallback, so "Sabotage" can never get clipped to "Sab…". */}
      <div
        className="flex gap-1.5 overflow-x-auto no-scrollbar -mr-1 pr-4"
        style={{
          WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 16px), transparent)',
          maskImage: 'linear-gradient(to right, black calc(100% - 16px), transparent)',
        }}
        role="tablist"
        aria-label="Transaction filter"
      >
        {FILTER_TYPES.map(f => (
          <Button
            key={f.id}
            variant={filter === f.id ? 'default' : 'outline'}
            size="sm"
            role="tab"
            aria-selected={filter === f.id}
            aria-label={f.label}
            className={cn("text-xs gap-1 h-7 px-2 py-1 shrink-0", filter === f.id && "shadow-sm")}
            onClick={() => setFilter(f.id)}
          >
            <span>{f.icon}</span>
            <span className="hidden min-[420px]:inline">{f.label}</span>
          </Button>
        ))}
      </div>

      {/* Search + Sort */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by player name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          className="h-9 gap-1.5 text-xs"
          onClick={() => setSortNewest(prev => !prev)}
        >
          <ArrowDownUp className="w-3.5 h-3.5" />
          {sortNewest ? 'Newest' : 'Oldest'}
        </Button>
      </div>

      {/* Transaction List */}
      <Card className="bg-card border-border overflow-hidden">
        {loading ? (
          <CardContent className="p-0">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-4 border-b border-border/40">
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
                <Skeleton className="h-3 w-16" />
              </div>
            ))}
          </CardContent>
        ) : activities.length === 0 ? (
          <CardContent className="p-12 text-center">
            <Filter className="w-8 h-8 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No transactions found</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              {filter !== 'all' || search ? 'Try adjusting your filters' : 'Transactions will appear here as they happen'}
            </p>
          </CardContent>
        ) : (
          <CardContent className="p-0">
            {activities.map(renderTransaction)}

            {hasMore && (
              <div className="p-4 text-center border-t border-border/40">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1.5 text-xs text-primary"
                  onClick={() => fetchActivities(true)}
                  disabled={loadingMore}
                >
                  <ChevronDown className="w-3.5 h-3.5" />
                  {loadingMore ? 'Loading...' : 'Load more'}
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Summary */}
      {!loading && activities.length > 0 && (
        <p className="text-[10px] text-muted-foreground/60 text-center">
          Showing {activities.length} transaction{activities.length !== 1 ? 's' : ''}
          {hasMore ? ', scroll for more' : ''}
        </p>
      )}
    </div>
  );
};

export default MLBBetaTransactionHistory;